import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import { formatUuidT } from "../../flatbuffers/display";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const channelEndpointAPI = createApi({
  reducerPath: "channelEndpointAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetChannelEndpoints"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /** GET /api/channel_endpoints/?page=N&limit=N[&channel_id=uuid][&server_id=uuid] */
    getChannelEndpoints: builder.query({
      query: (body: {
        page: number;
        limit: number;
        token: string;
        channelId?: string;
        serverId?: string;
        searchAndFilterString?: string;
      }) => {
        const filters: string[] = [];
        if (body.channelId) filters.push(`filter_target=channel_id&filter=${body.channelId}`);
        if (body.serverId) filters.push(`filter_target=server_id&filter=${body.serverId}`);
        const filterStr = filters.length > 0 ? `&${filters.join("&")}` : "";
        return {
          url: `/channel_endpoints/?sort=created_at&sort_direction=desc&page=${body.page}&limit=${
            body.limit
          }${filterStr}${body.searchAndFilterString ?? ""}`,
          headers: { authorization: body.token },
        };
      },
      providesTags: ["tagGetChannelEndpoints"],
    }),

    /** GET /api/channel_endpoints/:id */
    getChannelEndpoint: builder.query({
      query: (body: { id: string; token: string }) => ({
        url: `/channel_endpoints/${body.id}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannelEndpoints"],
    }),

    /** POST /api/channel_endpoints/ */
    postChannelEndpoint: builder.mutation({
      query: (body: { body: Record<string, unknown>; token: string }) => ({
        url: "/channel_endpoints/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateChannelEndpoint" },
      invalidatesTags: ["tagGetChannelEndpoints"],
    }),

    /** PUT /api/channel_endpoints/:id */
    putChannelEndpoint: builder.mutation({
      query: (body: { body: Record<string, unknown>; token: string }) => ({
        url: `/channel_endpoints/${formatUuidT(body.body.id as string)}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateChannelEndpoint" },
      invalidatesTags: ["tagGetChannelEndpoints"],
    }),

    /** DELETE /api/channel_endpoints/:id */
    deleteChannelEndpoint: builder.mutation({
      query: (body: { id: string; token: string }) => ({
        url: `/channel_endpoints/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetChannelEndpoints"],
    }),

    /** GET /api/channels/:channelID/endpoints (sub-resource, ordered by group_id, priority) */
    getChannelEndpointsByChannel: builder.query({
      query: (body: { channelId: string; token: string }) => ({
        url: `/channels/${body.channelId}/endpoints`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannelEndpoints"],
    }),

    /** PUT /api/channels/:channelID/endpoints/bulk (atomic replace-all) */
    bulkPutChannelEndpoints: builder.mutation({
      query: (body: { channelId: string; body: Record<string, unknown>[]; token: string }) => ({
        url: `/channels/${body.channelId}/endpoints/bulk`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetChannelEndpoints"],
    }),
  }),
});

export const {
  useLazyGetChannelEndpointsQuery,
  useLazyGetChannelEndpointQuery,
  usePostChannelEndpointMutation,
  usePutChannelEndpointMutation,
  useDeleteChannelEndpointMutation,
  useLazyGetChannelEndpointsByChannelQuery,
  useBulkPutChannelEndpointsMutation,
} = channelEndpointAPI;
